import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ───────────────────────────────────────────────────────────────

export type CmmsWOStatus = "open" | "in_progress" | "on_hold" | "completed" | "verified" | "cancelled";
export type CmmsWOType = "corrective" | "preventive" | "inspection" | "project";

export interface CmmsWorkOrder {
  id: string;
  org_id: string;
  wo_number: string;
  title: string;
  description: string | null;
  wo_type: CmmsWOType;
  status: CmmsWOStatus;
  priority: number;
  asset_id: string | null;
  assigned_to: string | null;
  pm_schedule_id: string | null;
  due_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  verified_at: string | null;
  estimated_hours: number | null;
  notes: string | null;
  created_by: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CmmsWOPartUsed {
  id: string;
  org_id: string;
  wo_id: string;
  part_id: string;
  quantity: number;
  unit_cost: number | null;
  notes: string | null;
  used_by: string | null;
  created_at: string;
}

export interface CmmsWOLabor {
  id: string;
  org_id: string;
  wo_id: string;
  user_id: string | null;
  hours: number;
  description: string | null;
  work_date: string;
  created_at: string;
}

export interface CreateCmmsWOInput {
  title: string;
  description?: string;
  wo_type?: CmmsWOType;
  priority?: number;
  asset_id?: string | null;
  assigned_to?: string | null;
  due_date?: string;
  estimated_hours?: number;
  notes?: string;
}

export interface UpdateCmmsWOInput {
  title?: string;
  description?: string | null;
  wo_type?: CmmsWOType;
  status?: CmmsWOStatus;
  priority?: number;
  asset_id?: string | null;
  assigned_to?: string | null;
  due_date?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  verified_at?: string | null;
  estimated_hours?: number | null;
  notes?: string | null;
}

export interface LogPartsUsedInput {
  wo_id: string;
  part_id: string;
  quantity: number;
  unit_cost?: number;
  notes?: string;
}

export interface LogLaborInput {
  wo_id: string;
  user_id?: string | null;
  hours: number;
  description?: string;
  work_date?: string;
}

// ── Queries ─────────────────────────────────────────────────────────────

export function useCmmsWorkOrders(filters?: {
  status?: CmmsWOStatus;
  wo_type?: CmmsWOType;
  asset_id?: string;
  assigned_to?: string;
  search?: string;
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["cmms-work-orders", filters],
    queryFn: async () => {
      let query = supabase
        .from("cmms_work_order" as any)
        .select("*")
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (filters?.status) query = query.eq("status", filters.status);
      if (filters?.wo_type) query = query.eq("wo_type", filters.wo_type);
      if (filters?.asset_id) query = query.eq("asset_id", filters.asset_id);
      if (filters?.assigned_to) query = query.eq("assigned_to", filters.assigned_to);
      if (filters?.search) query = query.or(`title.ilike.%${filters.search}%,wo_number.ilike.%${filters.search}%`);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as CmmsWorkOrder[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

export function useCmmsWorkOrder(id: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["cmms-work-orders", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cmms_work_order" as any)
        .select("*")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data as CmmsWorkOrder;
    },
    enabled: !!id && isAuthenticated && !authLoading,
  });
}

export function useCmmsWOPartsUsed(woId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["cmms-wo-parts", woId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cmms_wo_part_used" as any)
        .select("*, part:part_id(*)")
        .eq("wo_id", woId!);
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!woId && isAuthenticated && !authLoading,
  });
}

export function useCmmsWOLabor(woId: string | null) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["cmms-wo-labor", woId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cmms_wo_labor" as any)
        .select("*")
        .eq("wo_id", woId!)
        .order("work_date", { ascending: false });
      if (error) throw error;
      return (data || []) as CmmsWOLabor[];
    },
    enabled: !!woId && isAuthenticated && !authLoading,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useCreateCmmsWO() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateCmmsWOInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      // Generate WO number via RPC
      const { data: woNumber, error: numError } = await supabase
        .rpc("generate_cmms_wo_number", { p_org_id: userData.org_id });
      if (numError) throw numError;

      const { data, error } = await supabase
        .from("cmms_work_order" as any)
        .insert([{
          org_id: userData.org_id,
          wo_number: woNumber,
          title: input.title,
          description: input.description || null,
          wo_type: input.wo_type || "corrective",
          priority: input.priority || 2,
          asset_id: input.asset_id || null,
          assigned_to: input.assigned_to || null,
          due_date: input.due_date || null,
          estimated_hours: input.estimated_hours || null,
          notes: input.notes || null,
          created_by: userData.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as CmmsWorkOrder;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cmms-work-orders"] });
      toast({ title: "Work order created", description: `${data.wo_number} has been created.` });
    },
    onError: (error: Error) => {
      toast({ title: "Error creating work order", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateCmmsWO() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateCmmsWOInput }) => {
      // Auto-set timestamps based on status changes
      const updates: any = { ...input };
      if (input.status === "in_progress" && !input.started_at) {
        updates.started_at = new Date().toISOString();
      }
      if (input.status === "completed" && !input.completed_at) {
        updates.completed_at = new Date().toISOString();
      }
      if (input.status === "verified" && !input.verified_at) {
        updates.verified_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from("cmms_work_order" as any)
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as CmmsWorkOrder;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cmms-work-orders"] });
      toast({ title: "Work order updated", description: "The work order has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error updating work order", description: error.message, variant: "destructive" });
    },
  });
}

export function useLogPartsUsed() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: LogPartsUsedInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("cmms_wo_part_used" as any)
        .insert([{
          org_id: userData.org_id,
          wo_id: input.wo_id,
          part_id: input.part_id,
          quantity: input.quantity,
          unit_cost: input.unit_cost || null,
          notes: input.notes || null,
          used_by: userData.id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as CmmsWOPartUsed;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cmms-wo-parts", data.wo_id] });
      queryClient.invalidateQueries({ queryKey: ["parts"] });
      toast({ title: "Part logged", description: "Part usage has been recorded and stock deducted." });
    },
    onError: (error: Error) => {
      toast({ title: "Error logging part", description: error.message, variant: "destructive" });
    },
  });
}

export function useLogLabor() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: LogLaborInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id, id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("cmms_wo_labor" as any)
        .insert([{
          org_id: userData.org_id,
          wo_id: input.wo_id,
          user_id: input.user_id || userData.id,
          hours: input.hours,
          description: input.description || null,
          work_date: input.work_date || new Date().toISOString().split("T")[0],
        }])
        .select()
        .single();

      if (error) throw error;
      return data as CmmsWOLabor;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cmms-wo-labor", data.wo_id] });
      toast({ title: "Labor logged", description: "Labor time has been recorded." });
    },
    onError: (error: Error) => {
      toast({ title: "Error logging labor", description: error.message, variant: "destructive" });
    },
  });
}
